import ReactDom from 'react-dom';

import { useMutation, useQuery, useQueryClient } from 'react-query';

import { Controller, useForm } from 'react-hook-form';

import { State, useState } from '@hookstate/core';
import { yupResolver } from '@hookform/resolvers/yup';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { CustomPicker } from '@components/CustomPickerWithBorder/CustomPicker';

import { createStudioEquipmentStatus } from '@services/queries/studios';

import { EquipmentStatusCategoryOptions } from '@models/EquipmentStatus.model';

import { useState as classicUseState, useContext } from 'react';
import { NavigationContext } from '@layout/navigation/navigation';

import { showDefaultStatus } from '@services/queries/studios/showDefaultStatus/showDefaultStatus';
import { DeleteDialog, Toggle } from '@components/index';
import { updateDefaultStatus } from '@services/queries/studios/updateDefaultStatus/updateDefaultStatus';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Confirm,
  Label,
  Line,
  Input,
  Form,
  Divider,
  Textarea,
  Error,
  LabelOptional,
  RowDiv,
  YesNo,
} from './CreateEquipmentStatus.styles';
import { formSchema, typeOptions } from '../../CustomStatus.utils';

interface ICreateEquipmentStatus {
  open: State<boolean>;
}

interface IFormData {
  name: string;
  description: string;
  type: EquipmentStatusCategoryOptions;
  isDefault: boolean;
}

export const CreateEquipmentStatus = ({ open }: ICreateEquipmentStatus) => {
  const portalDiv = document.getElementById('portal');

  const { studioId } = useContext(NavigationContext);

  const openDeleteDialog = useState<boolean>(false);

  const queryClient = useQueryClient();

  const defaultStatus = useQuery(['defaultStatus', studioId], () =>
    showDefaultStatus({ id: studioId }),
  );

  const [defaultStatusState, setDefaultStatusState] =
    classicUseState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    control,
    setError,
    reset,
    clearErrors,
  } = useForm<IFormData>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
    },
    mode: 'onChange',
  });

  const close = () => {
    open.set(false);
    reset();
    clearErrors('name');
    setDefaultStatusState(false);
  };

  const patchDefaultStatusMutation = useMutation(updateDefaultStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries(['defaultStatus', studioId]);
    },
  });

  const createEquipmentStatusMutation = useMutation(
    createStudioEquipmentStatus,
    {
      onSuccess: ({ id }) => {
        queryClient.invalidateQueries(['studios', studioId, 'customStatus']);
        queryClient.invalidateQueries(['defaultStatus', studioId]);
        if (defaultStatusState) {
          patchDefaultStatusMutation.mutate({
            id: studioId,
            equipmentStatusId: id,
          });
        }
        close();
      },
      onError: (error: any) => {
        if (
          error.response?.data?.message ===
          'There is already a custom field with this name'
        ) {
          setError('name', {
            type: 'validate',
            message: 'There is already a custom field with this name',
          });
        }
      },
    },
  );

  const onSubmit = (data: IFormData) => {
    createEquipmentStatusMutation.mutate({
      id: studioId,
      name: data.name,
      description: data.description,
      category: data.type,
    });
  };

  const toggleDefaultStatus = () => {
    if (defaultStatus.data && !defaultStatusState) {
      openDeleteDialog.set(true);
    }
    return setDefaultStatusState(!defaultStatusState);
  };

  return open.get() && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>Add Custom Status</Title>
              <Close onClick={close}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>
            <Divider />
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Label>Name</Label>

              <Input
                type="text"
                autoFocus
                placeholder="Choose a Custom Status Name"
                {...register('name')}
                className="infoInputs"
                isError={
                  errors.name &&
                  errors.name.message ===
                    'There is already a custom status with this name'
                }
              />

              {errors.name &&
                errors.name.message ===
                  'There is already a custom status with this name' && (
                  <Error>{errors.name.message}</Error>
                )}

              <LabelOptional>Description (Optional)</LabelOptional>

              <Textarea
                placeholder="Placeholder"
                {...register('description')}
                className="infoInputs"
              />

              <Label>Type</Label>

              <Controller
                control={control}
                name="type"
                render={({ field: { onChange, value } }) => (
                  <CustomPicker
                    options={typeOptions}
                    value={typeOptions.find(c => c.value === value)}
                    onChange={(val: any) => {
                      onChange(val.value);
                    }}
                    placeholder="Select a field type"
                  />
                )}
              />

              <Label>Default Status</Label>

              <Label className="description">
                If set, this status will be selected when adding assets
              </Label>

              <RowDiv>
                <Toggle
                  selected={defaultStatusState}
                  toggleSelected={() => {
                    toggleDefaultStatus();
                  }}
                />
                <YesNo>{defaultStatusState ? 'Yes' : 'No'}</YesNo>
              </RowDiv>

              <Line>
                <Confirm disabled={!isValid} type="submit">
                  Add
                </Confirm>
              </Line>
            </Form>

            <DeleteDialog
              open={openDeleteDialog}
              onDelete={() => {
                setDefaultStatusState(true);
                openDeleteDialog.set(false);
              }}
              backgroundColor="#FF9900"
              buttonText="Replace Default Status"
              text="There is already one status set as default"
              onCancel={() => setDefaultStatusState(false)}
            />
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
